/** @jsx jsx */
import { jsx } from "theme-ui";
import React, { useEffect, useState } from "react";
import { ModalProvider } from "react-modal-hook";
import { Router } from "react-router-dom";
import I18nProvider from "providers/I18nProvider/I18nProvider";
import { Provider } from 'react-redux';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import AuthenticationProvider from "providers/AuthenticationProvider/AuthenticationProvider";
import AppSettingsProvider from "providers/AppSettingsProvider/AppSettingsProvider";
import LicenseProvider from "providers/LicenseProvider/LicenseProvider";
import { ToastProvider, DefaultToastContainer } from "react-toast-notifications";
import Routes from "routes/Routes";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import history from "utils/history";
import { Provider as ThemeProvider } from "./ThemeProvider";
import TimeProvider from "providers/TimeProvider/TimeProvider";
import { AppInsightsContextProvider, useAppInsights } from "providers/AppinsightsProvider/AppInsightsProvider";
import { QueryClientProvider } from "react-query";
import { queryClient } from "./api/queryClient";
import { ReactQueryDevtools } from "react-query/devtools";
import store from './store/store'
import keycloak, { KEYCLOAK_SCOPE } from './keycloak'
import { initLicense } from "store/license";

import "App.scss";
import { authenticateFailure, setAuthenticated, validateRefreshToken } from "store/authentication";
import { LICENSE_ID_STORAGE_KEY, UPGRADE_OFFLINE_TOKEN_ATTEMPT_KEY } from "config/constants";
import { KeycloakInitOptions } from "keycloak-js";
import { CenterPageLoading } from "components/Loading/Loading";
import { getPersistedOfflineToken, persistOfflineToken } from "utils/common";
import { authorise } from "store/authorisation";

enum KeyclaokEventEnum {
    'AuthSuccess',
    'AuthRefreshSuccess',
    'AuthRefreshError',
    'AuthError',
    'AuthLogout'
}

const App: React.FC = () => {
    const [keycloakInitialized, setKeycloakInitialized] = useState(false);
    const appInsights = useAppInsights();

    useEffect(() => {
        store.dispatch(initLicense());
        try {
            const refreshToken = getPersistedOfflineToken();

            const initOptions: KeycloakInitOptions = {
                redirectUri: `${window.location.origin}/#/auth?check=1`,
                ...(refreshToken ? { refreshToken } : {}),
                onLoad: undefined,
                checkLoginIframe: false,
                scope: KEYCLOAK_SCOPE,
            }


            keycloak.init(initOptions)
                .then((authenticated) => {
                    store.dispatch(setAuthenticated(authenticated))
                    if (authenticated) {
                        const refreshTokenParams = keycloak.refreshTokenParsed;
                        const isOffline = refreshTokenParams?.typ === 'Offline';
                        if (isOffline && keycloak.refreshToken) {
                            persistOfflineToken(keycloak.refreshToken)
                        }
                        store.dispatch(authorise())
                    } else if (refreshToken) {
                        store.dispatch(validateRefreshToken())
                    }

                    // PROCEED WITH APP LOADING
                    setKeycloakInitialized(true);
                })
                .catch((err) => {
                    setKeycloakInitialized(true);
                    store.dispatch(authenticateFailure(err))
                });
        } catch (err) {
            store.dispatch(authenticateFailure(err))
        }

        const handleAuthEvent = (event_type: KeyclaokEventEnum, data?: any) => {
            store.dispatch(
                setAuthenticated(!!keycloak.authenticated)
            );

            const refreshTokenParams = keycloak.refreshTokenParsed;
            const isOffline = refreshTokenParams?.typ === 'Offline';

            if (
                isOffline &&
                keycloak.refreshToken &&
                (event_type === KeyclaokEventEnum.AuthSuccess || event_type === KeyclaokEventEnum.AuthRefreshSuccess)
            ) {
                persistOfflineToken(keycloak.refreshToken);
            }

            if (event_type === KeyclaokEventEnum.AuthError || event_type === KeyclaokEventEnum.AuthRefreshError) {
                appInsights.trackException({
                    exception: new Error(`Keycloak error. KeycloakEventEnum - ${event_type} `),
                    properties: {
                        error: data,
                        state: store.getState(),
                        licenseId: localStorage.getItem(LICENSE_ID_STORAGE_KEY),
                    }
                });
            } else {
                appInsights.trackTrace({
                    message: `Keycloak error. KeycloakEventEnum - ${event_type} `,
                    properties: {
                        error: data,
                        state: store.getState(),
                        licenseId: localStorage.getItem(LICENSE_ID_STORAGE_KEY),
                    }
                });

            }
        };

        keycloak.onAuthSuccess = () => handleAuthEvent(KeyclaokEventEnum.AuthSuccess);
        keycloak.onAuthRefreshSuccess = () => handleAuthEvent(KeyclaokEventEnum.AuthRefreshSuccess);
        keycloak.onAuthRefreshError = () => handleAuthEvent(KeyclaokEventEnum.AuthRefreshError);
        keycloak.onAuthLogout = () => handleAuthEvent(KeyclaokEventEnum.AuthLogout);
        keycloak.onAuthError = () => handleAuthEvent(KeyclaokEventEnum.AuthError);

        return () => {
            keycloak.onAuthSuccess = undefined;
            keycloak.onAuthRefreshSuccess = undefined;
            keycloak.onAuthRefreshError = undefined;
            keycloak.onAuthLogout = undefined;
            keycloak.onAuthError = undefined;
        };
    }, []);


    return (
        <div className="app">
            {keycloakInitialized ? (
                <Provider store={store} >
                    <QueryClientProvider client={queryClient}>
                        <Router history={history}>
                            <AppInsightsContextProvider>
                                <I18nProvider>
                                    <AppSettingsProvider>
                                        <ThemeProvider>
                                            <ToastProvider
                                                placement="top-center"
                                                components={{
                                                    ToastContainer: (props) => (
                                                        <DefaultToastContainer
                                                            {...props}
                                                            data-testid="toast-messages"
                                                            // @ts-ignore
                                                            style={{ zIndex: 3100 }}
                                                        />
                                                    )
                                                }}
                                            >
                                                <ModalProvider>
                                                    <AuthenticationProvider>
                                                        <TimeProvider>
                                                            <LicenseProvider>
                                                                <ErrorBoundary>
                                                                    <Routes />
                                                                </ErrorBoundary>
                                                            </LicenseProvider>
                                                        </TimeProvider>
                                                    </AuthenticationProvider>
                                                </ModalProvider>
                                            </ToastProvider>
                                        </ThemeProvider>
                                    </AppSettingsProvider>
                                </I18nProvider>
                            </AppInsightsContextProvider>
                        </Router>
                        <ReactQueryDevtools initialIsOpen={false} position="top-right" />
                    </QueryClientProvider>
                </Provider>
            ) : <CenterPageLoading />}
        </div>
    );
};

export default App;
